import { sendGAEvent } from "@next/third-parties/google";

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/**
 * Capture UTM parameters from URL and store in sessionStorage
 */
export const captureUtmParams = () => {
  if (typeof window === "undefined") return;
  const urlParams = new URLSearchParams(window.location.search);
  const utms: UtmParams = {};

  UTM_KEYS.forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utms[key] = value;
      sessionStorage.setItem(key, value);
    }
  });

  return utms;
};

/**
 * Get stored UTM parameters from sessionStorage
 */
export const getStoredUtmParams = (): UtmParams => {
  if (typeof window === "undefined") return {};
  const utms: UtmParams = {};
  UTM_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value) {
      utms[key] = value;
    }
  });
  return utms;
};

/**
 * Get Google Analytics Identifiers (Client ID & Session ID)
 */
export const getGaIdentifiers = (): Promise<{
  clientId?: string;
  sessionId?: string;
}> => {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.gtag) {
      resolve({});
      return;
    }

    try {
      // Find the first measurement ID if possible, or use the one from env
      const gaId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
      if (!gaId) {
        resolve({});
        return;
      }

      let clientId: string | undefined;
      let sessionId: string | undefined;

      window.gtag("get", gaId, "client_id", (id: string) => {
        clientId = id;
        if (sessionId !== undefined) resolve({ clientId, sessionId });
      });

      window.gtag("get", gaId, "session_id", (id: string) => {
        sessionId = id;
        if (clientId !== undefined) resolve({ clientId, sessionId });
      });

      // Timeout fallback if gtag doesn't respond
      setTimeout(() => {
        resolve({ clientId, sessionId });
      }, 2000);
    } catch (e) {
      console.error("Error fetching GA identifiers:", e);
      resolve({});
    }
  });
};

export const formatProductForAnalytics = (product: {
  count?: number;
  final_price_calculated?: string | number;
  product_id?: string | number;
  name?: string;
  vendor_name?: string;
  product_type_name?: string;
  product_subtype_name?: string;
  selectedVariant?: { weight?: string | number };
  selectedFlavor?: { name?: string };
}) => {
  const quantity = product.count || 1;
  const totalPrice = Number(product.final_price_calculated) || 0;
  const unitPrice = totalPrice / quantity;

  return {
    item_id: String(product.product_id),
    item_name: product.name,
    item_brand: product.vendor_name,

    item_category: product.product_type_name,
    item_category2: product.product_subtype_name,
    price: unitPrice,
    item_variant: `${product?.selectedVariant?.weight ? `${product.selectedVariant.weight} kg |` : ""}${product?.selectedFlavor?.name ? `${product.selectedFlavor.name}` : ""}`,
    quantity: quantity,
  };
};

export const isAuthPersistent = () => {
  if (typeof window === "undefined") return false;
  // User platform auth is localStorage-only
  return !!localStorage.getItem("accessToken");
};

export const sendCartActionEvent = ({
  eventName,
  product,
  isAuthenticated,
}: {
  eventName: "add_to_cart" | "remove_from_cart";
  product: Parameters<typeof formatProductForAnalytics>[0];
  isAuthenticated: boolean;
}) => {
  // Use consolidated status to avoid race conditions during hydration
  const authStatus =
    isAuthenticated || isAuthPersistent() ? "logged_in" : "logged_out";

  sendGAEvent("event", eventName, {
    currency: "INR",
    value: Number(product.final_price_calculated),
    items: [formatProductForAnalytics(product)],
    user_auth_state: authStatus,
  });
};

/**
 * Restore tracking identifiers from a payload (e.g. payment status response)
 * into sessionStorage and GA config to ensure attribution continuity.
 */
export const restoreTrackingFromPayload = (
  payload: Record<string, unknown> | null,
) => {
  if (typeof window === "undefined" || !payload || typeof payload !== "object")
    return;

  // Restore UTMs to sessionStorage
  UTM_KEYS.forEach((key) => {
    const orderPlacedPayload = payload.order_placed_payload as
      | Record<string, unknown>
      | undefined;
    const value = payload[key] || orderPlacedPayload?.[key];
    if (value && typeof value === "string") {
      sessionStorage.setItem(key, value);
    }
  });

  // Re-sync GA identifiers if returned from backend
  const gaId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
  const orderPlacedPayload = payload.order_placed_payload as
    | Record<string, unknown>
    | undefined;
  const sessionId =
    (payload.ga_session_id as string | undefined) ||
    (orderPlacedPayload?.ga_session_id as string | undefined);

  if (window.gtag && gaId && sessionId) {
    window.gtag("config", gaId, {
      session_id: sessionId,
      update: true, // Tells GA to update current configuration
    });
  }
};

/**
 * Format phone number to +91 format if available
 */
export const formatPhoneNumber = (
  phone?: string | null,
): string | undefined => {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, "");
  if (!digits) return undefined;

  // 10-digit standard Indian mobile
  if (digits.length === 10) {
    return `+91${digits}`;
  }
  // 11-digit starting with 0
  if (digits.length === 11 && digits.startsWith("0")) {
    return `+91${digits.slice(1)}`;
  }
  // 12-digit starting with 91
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+${digits}`;
  }
  // Other numbers starting with +
  if (phone.trim().startsWith("+")) {
    return `+${digits}`;
  }
  // Fallback: prepend +91
  return `+91${digits}`;
};

export interface PushIdentifyParams {
  email: string;
  phone?: string | null;
  firstName?: string | null;
  source: "popup" | "checkout";
}

/**
 * Push tf_identify event to dataLayer for Klaviyo identification
 */
export const pushIdentifyToDataLayer = ({
  email,
  phone,
  firstName,
  source,
}: PushIdentifyParams) => {
  if (typeof window === "undefined") return;

  const trimmedEmail = email?.trim();
  if (!trimmedEmail) return;

  const formattedPhone = formatPhoneNumber(phone);
  const trimmedFirstName = firstName?.trim();

  const payload: Record<string, unknown> = {
    event: "tf_identify",
    tf_email: trimmedEmail,
  };

  if (source === "checkout" && trimmedFirstName) {
    payload.tf_first_name = trimmedFirstName;
  }

  if (formattedPhone) {
    payload.tf_phone = formattedPhone;
  }

  payload.tf_source = source;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};
